import { useEffect, useState } from "react";
import { Check, ExternalLink, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { api, docUrl, errMsg } from "@/lib/api";
import { DOC_CATEGORIES, DOC_STATUS, DOC_TONE, fmtDate, fmtEuro } from "@/lib/format";
import { Btn, Empty, Modal, PageHeader, Pill, Spinner } from "@/components/rp";

const QuoteCard = ({ d, onDecide }) => (
  <div className="rp-card p-6 sm:p-8 bg-[#E6DDCE]/40 border-[#C8BDAB]" data-testid={`pending-quote-${d.id}`}>
    <Pill tone="warning">Da approvare</Pill>
    <p className="font-medium mt-4">{d.title}</p>
    <p className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2" data-testid="pending-quote-amount">{fmtEuro(d.amount)}</p>
    <p className="text-xs text-[#757575] mt-2">Caricato il {fmtDate(d.created_at)}</p>
    <div className="flex flex-wrap gap-2 mt-6">
      <a href={docUrl(d)} target="_blank" rel="noreferrer"><Btn variant="outline" data-testid="quote-view-button"><ExternalLink className="h-4 w-4" /> Visualizza PDF</Btn></a>
      <Btn variant="danger" onClick={() => onDecide(d, false)} data-testid="quote-reject-button"><X className="h-4 w-4" /> Rifiuta</Btn>
      <Btn onClick={() => onDecide(d, true)} data-testid="quote-accept-button"><Check className="h-4 w-4" /> Accetta</Btn>
    </div>
  </div>
);

const DocRow = ({ d }) => (
  <a href={docUrl(d)} target="_blank" rel="noreferrer" data-testid={`document-row-${d.id}`} className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-[#EBEBEB] hover:bg-[#F7F7F7] transition-colors">
    <span className="h-10 w-10 rounded-xl bg-[#EBEBEB] flex items-center justify-center shrink-0"><FileText className="h-5 w-5" strokeWidth={1.5} /></span>
    <span className="flex-1 min-w-0">
      <span className="block font-medium truncate">{d.title}</span>
      <span className="block text-xs text-[#757575]">{fmtDate(d.created_at)}{d.amount != null && ` · ${fmtEuro(d.amount)}`}</span>
    </span>
    {d.status !== "none" && <Pill tone={DOC_TONE[d.status]}>{DOC_STATUS[d.status]}</Pill>}
    <ExternalLink className="h-4 w-4 text-[#757575] shrink-0" />
  </a>
);

export default function ClientDocumenti() {
  const [docs, setDocs] = useState(null);
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const confirm = async () => {
    setSaving(true);
    try {
      await api.post(`/documents/${decision.d.id}/${decision.accept ? "accept" : "reject"}`, { note });
      toast.success(decision.accept ? "Preventivo accettato. Grazie!" : "Preventivo rifiutato. Lo staff è stato avvisato.");
      setDecision(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (!docs) return <Spinner />;
  const pending = docs.filter((d) => d.category === "preventivo" && d.status === "pending");

  return (
    <div>
      <PageHeader label="Documenti" title="Preventivi e documenti" />
      {pending.length > 0 && (
        <section className="mb-10 space-y-4">
          <p className="rp-label">Da approvare</p>
          <div className="grid lg:grid-cols-2 gap-4">{pending.map((d) => <QuoteCard key={d.id} d={d} onDecide={(doc, accept) => setDecision({ d: doc, accept })} />)}</div>
        </section>
      )}
      {!docs.length && <Empty icon={FileText} title="Nessun documento" text="Preventivi, progetti e pratiche saranno disponibili qui." data-testid="documents-empty" />}
      <div className="space-y-8">
        {DOC_CATEGORIES.map((cat) => {
          const items = docs.filter((d) => d.category === cat.v);
          if (!items.length) return null;
          return (
            <section key={cat.v} data-testid={`documents-category-${cat.v}`}>
              <p className="rp-label mb-3">{cat.l}</p>
              <div className="grid lg:grid-cols-2 gap-2">{items.map((d) => <DocRow key={d.id} d={d} />)}</div>
            </section>
          );
        })}
      </div>
      <Modal open={!!decision} onOpenChange={(o) => !o && setDecision(null)} testid="quote-decision-dialog"
        title={decision?.accept ? "Accetta il preventivo" : "Rifiuta il preventivo"}
        description={decision && `${decision.d.title} · ${fmtEuro(decision.d.amount)}`}>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="rp-field" placeholder="Nota per lo staff (facoltativa)" data-testid="quote-note-input" />
        <div className="flex justify-end gap-2">
          <Btn variant="secondary" onClick={() => setDecision(null)}>Annulla</Btn>
          <Btn variant={decision?.accept ? "primary" : "danger"} loading={saving} onClick={confirm} data-testid="quote-confirm-button">
            {decision?.accept ? "Conferma accettazione" : "Conferma rifiuto"}
          </Btn>
        </div>
      </Modal>
    </div>
  );
}
